import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaHeart, FaTimes } from 'react-icons/fa';
import BottomNavBar from '../components/BottomNavBar';
import getImageUrl from '../utils/imageUrl';

const API_URL = process.env.REACT_APP_API_URL || 'https://edulove-backend.onrender.com/api';

function LikesPage({ user }) {
  const [likes, setLikes] = useState([]);
  const [view, setView] = useState('received'); // 'received' | 'sent'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [imageErrors, setImageErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchLikes();
  }, []);

  useEffect(() => {
    fetchLikes();
  }, [view]);

  const handleImageError = (userId) => {
    setImageErrors(prev => ({ ...prev, [userId]: true }));
  };

  const handleForbiddenRedirect = (err) => {
    const msg = err.response?.data?.error || '';
    const profileCompletion = err.response?.data?.profileCompletion;
    const required = err.response?.data?.required;
    
    if (err.response?.status === 403 && msg.toLowerCase().includes('complete')) {
      if (profileCompletion !== undefined && required !== undefined) {
        alert(`⚠️ Profile Incomplete\n\nYour profile is ${profileCompletion}% complete.\nYou need at least ${required}% to use this feature.\n\nPlease complete your profile first.`);
      } else {
        alert(msg);
      }
      navigate('/profile');
      return true;
    }
    return false;
  };

  const fetchLikes = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      const endpoint = view === 'sent' ? `${API_URL}/matches/likes/sent` : `${API_URL}/matches/likes`;
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const likesData = Array.isArray(response.data.likes) ? response.data.likes : (response.data || []);
      setLikes(likesData);
      setError('');
      // If viewing received likes, mark them as seen so badge clears
      try {
        const token = localStorage.getItem('token');
        if (token && view === 'received') {
          await axios.post(`${API_URL}/matches/mark-likes-seen`, {}, { headers: { Authorization: `Bearer ${token}` } });
          try { sessionStorage.removeItem('nav_counts_cache_v1'); } catch (e) {}
          try { window.__REFRESH_NAV_COUNTS__?.(); } catch (e) {}
        }
      } catch (err) {
        // ignore
      }
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      if (!handleForbiddenRedirect(err)) setError(err.response?.data?.error || 'Failed to load likes');
      console.error('Error fetching likes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (matchId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/matches/cancel-like/${matchId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Remove from likes list
      setLikes(likes.filter(l => l._id !== matchId));
      setError('');
    } catch (err) {
      if (!handleForbiddenRedirect(err)) setError(err.response?.data?.error || 'Error cancelling like');
    }
  };

  const handleLikeBack = async (likeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/matches/like-back/${likeId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Remove from likes list
      setLikes(likes.filter(l => l._id !== likeId));
      setError('');

      // If backend returned a match, navigate to the chat for that match
      if (response.data?.match?._id) {
        navigate(`/chat/${response.data.match._id}`);
      }
    } catch (err) {
      if (!handleForbiddenRedirect(err)) setError(err.response?.data?.error || 'Error liking back');
    }
  };

  const handlePass = async (likeId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/matches/pass-like/${likeId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Remove from likes list
      setLikes(likes.filter(l => l._id !== likeId));
      setError('');
    } catch (err) {
      if (!handleForbiddenRedirect(err)) setError(err.response?.data?.error || 'Error passing');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading likes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-orange-50 pb-24">
      <div className="max-w-4xl mx-auto p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 mt-4">
          <button
            onClick={() => navigate('/discover')}
            className="text-pink-500 hover:text-pink-600"
          >
            <FaArrowLeft size={24} />
          </button>
          <div className="flex flex-col items-center">
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <FaHeart className="text-red-500" /> {view === 'sent' ? 'Likes You Sent' : 'Likes You Got'}
            </h1>
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={() => setView('received')}
                className={`px-3 py-1 rounded ${view === 'received' ? 'bg-pink-500 text-white' : 'bg-white text-gray-600 border'}`}
              >
                Received
              </button>
              <button
                onClick={() => setView('sent')}
                className={`px-3 py-1 rounded ${view === 'sent' ? 'bg-pink-500 text-white' : 'bg-white text-gray-600 border'}`}
              >
                Sent
              </button>
            </div>
          </div>
          <div className="w-6"></div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {/* Likes List */}
        {likes.length === 0 ? (
          <div className="card text-center max-w-md mx-auto">
            <FaHeart size={48} className="text-pink-300 mx-auto mb-4" />
            <p className="text-gray-600 text-lg mb-4">No likes yet</p>
            <p className="text-gray-500 mb-6">Keep swiping to get likes from other users!</p>
            <button
              onClick={() => navigate('/discover')}
              className="btn-primary"
            >
              Start Discovering
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {likes.map((like) => (
              <div key={like._id} className="card overflow-hidden hover:shadow-xl transition">
                {/* Photo */}
                <div className="relative mb-4 bg-gray-300 rounded-lg overflow-hidden h-64">
                  {like.user?.photos && like.user.photos.length > 0 && !imageErrors[like.user._id] ? (
                    <img
                      src={getImageUrl(like.user.photos[0].url)}
                      alt={like.user.name}
                      className="w-full h-full object-cover"
                      onError={() => handleImageError(like.user._id)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-500 bg-gradient-to-br from-gray-300 to-gray-400">
                      📷 {imageErrors[like.user._id] ? 'Photo failed to load' : 'No photo'}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    ❤️ Likes You
                  </div>

                  {/* Online indicator */}
                  {like.user?.isOnline && (
                    <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                      🟢 Online
                    </div>
                  )}
                </div>

                {/* Info */}
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  {like.user?.nickname || like.user?.name}
                </h3>

                <p className={`text-xs font-medium mb-2 ${like.user?.isOnline ? 'text-green-600' : 'text-gray-500'}`}>
                  {like.user?.isOnline ? '🟢 Online' : '⚪ Offline'}
                </p>

                {like.user?.university && (
                  <p className="text-gray-600 mb-1">{like.user.university}</p>
                )}

                {like.user?.course && (
                  <p className="text-gray-500 text-sm mb-2">{like.user.course}</p>
                )}

                {/* Match Score */}
                {like.matchScore && (
                  <div className="mb-4 p-2 bg-pink-50 rounded-lg">
                    <p className="text-sm text-pink-700">
                      <strong>Compatibility: {like.matchScore.score}%</strong>
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  {view === 'sent' ? (
                    <>
                      <button
                        onClick={() => handleCancel(like._id)}
                        className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-300 transition flex items-center justify-center gap-2"
                      >
                        <FaTimes /> Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleLikeBack(like._id)}
                        className="flex-1 btn-primary flex items-center justify-center gap-2"
                      >
                        <FaHeart /> Like Back
                      </button>
                      <button
                        onClick={() => handlePass(like._id)}
                        className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-300 transition flex items-center justify-center gap-2"
                      >
                        <FaTimes /> Pass
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNavBar user={user} />
    </div>
  );
}

export default LikesPage;
